import Icon from '../Icon'

/**
 * How do you want to add this plant?
 *
 * Three routes, shown before the form rather than hidden inside it, because
 * they are three genuinely different jobs:
 *
 *   AI CAMERA     — Dillon is standing in front of the plant and does not know
 *                   what it is. Photograph it, get a suggestion, correct it.
 *   UPLOAD PHOTOS — the photographs already exist on the phone.
 *   ADD MANUALLY  — he knows exactly what it is and wants the keyboard, not
 *                   the camera. This is the fast path for a bench of the same
 *                   thing, and it must never be the slow one.
 *
 * Every route lands in the SAME form. This is a starting point, not a mode: a
 * plant begun manually can still be photographed, and one begun on the camera
 * can still be typed over. Anything else means choosing wrong costs a restart.
 */
export type AddRoute = 'camera' | 'upload' | 'manual'

export default function AddChooser({
  onChoose, onCancel, identifyReady,
}: {
  onChoose: (route: AddRoute) => void
  onCancel: () => void
  /** False when no identification key is configured, so the AI route is honest. */
  identifyReady: boolean
}) {
  return (
    <section className="card">
      <h2 style={{ fontSize: 26, marginTop: 0 }}>Add a plant</h2>
      <p className="muted small" style={{ marginTop: 0 }}>
        One record per physical pot. You can change any of this afterwards.
      </p>

      <div className="routes">
        <button type="button" onClick={() => onChoose('camera')}>
          <span className="route-icon"><Icon name="camera" size={22} /></span>
          <span>
            <b>AI Camera</b>
            <small>
              {identifyReady
                ? 'Photograph the plant and let it suggest what this is.'
                : 'Photograph the plant. Identification is not switched on yet.'}
            </small>
          </span>
          <Icon name="chevron" size={16} />
        </button>

        <button type="button" onClick={() => onChoose('upload')}>
          <span className="route-icon"><Icon name="image" size={22} /></span>
          <span>
            <b>Upload photos</b>
            <small>Pick photographs already on this phone.</small>
          </span>
          <Icon name="chevron" size={16} />
        </button>

        <button type="button" onClick={() => onChoose('manual')}>
          <span className="route-icon"><Icon name="edit" size={22} /></span>
          <span>
            <b>Add manually</b>
            <small>You know what it is. Straight to the details.</small>
          </span>
          <Icon name="chevron" size={16} />
        </button>
      </div>

      <button type="button" onClick={onCancel} style={{ marginTop: 16 }}>Cancel</button>
    </section>
  )
}
